// Shared helpers for all public forms (Blog, Contact, Partner, Demo, Checkout)

// True only when the value has at least one real (non-space) character
export const hasText = (value) =>
  typeof value === 'string' && value.trim().length > 0;

// Removes leading spaces while typing/pasting, so a field can never start with a space.
// (Spaces between words are still allowed.)
export const stripLeadingSpaces = (value) =>
  typeof value === 'string' ? value.replace(/^\s+/, '') : value;

// Returns a copy of the form data with every string value trimmed
export const trimFormData = (data) =>
  Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ])
  );

// Escapes text before it is placed inside an HTML email template, so anything a visitor
// types is shown as plain text and can never become markup or a link.
export const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\r?\n/g, '<br>');

// ---- Inline error messages ----

// "First name is required." when empty or spaces only
export const requiredError = (value, label) =>
  hasText(value) ? null : `${label} is required.`;

// ---- Field validators (return an error message, or null when valid) ----

const HAS_LETTER = /\p{L}/u;
const SPECIAL_CHARS_MSG = 'Emojis and unsupported special characters are not allowed';

// Person names: any language's letters (José, Müller, 李雷), digits, spaces and . ' ’ $ " & -
export function nameOnChangeValidator(inputValue, maxLength = 25) {
  // Allow empty / clearing input
  if (inputValue === undefined || inputValue == null || inputValue === "") {
    return null;
  }
  // Normalize en-dash / em-dash to hyphen
  inputValue = inputValue.replace(/[–—]/g, "-");
  const allowedRegex = /^[\p{L}\p{M}0-9 '’.$"&-]*$/u;
  if (!allowedRegex.test(inputValue)) {
    return SPECIAL_CHARS_MSG;
  }
  // Max length
  if (inputValue.length > maxLength) {
    return `Input cannot exceed ${maxLength} characters`;
  }
  return null;
}

// Company names and job titles: names plus , ( ) / # + @ : (e.g. "Acme, Inc.", "VP, Sales", "CEO/Founder")
export function companyOnChangeValidator(inputValue, maxLength = 50) {
  if (inputValue === undefined || inputValue == null || inputValue === "") {
    return null;
  }
  inputValue = inputValue.replace(/[–—]/g, "-");
  const allowedRegex = /^[\p{L}\p{M}0-9 '’.,$"&()\/#+@:-]*$/u;
  if (!allowedRegex.test(inputValue)) {
    return SPECIAL_CHARS_MSG;
  }
  if (inputValue.length > maxLength) {
    return `Input cannot exceed ${maxLength} characters`;
  }
  return null;
}

// Single-line free text such as a subject: letters, digits, spaces and normal punctuation
export function subjectOnChangeValidator(inputValue, maxLength = 100) {
  if (inputValue === undefined || inputValue == null || inputValue === "") {
    return null;
  }
  if (!/^[\p{L}\p{M}\p{N}\p{P}\p{Sc} +=<>~^|`]*$/u.test(inputValue)) {
    return SPECIAL_CHARS_MSG;
  }
  if (inputValue.length > maxLength) {
    return `Maximum ${maxLength} characters allowed`;
  }
  return null;
}

// Text areas: message, services, challenges.
// Letters (any language), digits, whitespace/new lines, punctuation (including / [ ] { } \ and
// curly quotes from phones), currency symbols and + = < > ~ ^ | `. Emojis are not allowed.
export function validateDescriptionField(inputValue, maxLength = 2500) {
  if (inputValue === undefined || inputValue == null || inputValue === "") {
    return null;
  }

  const allowedRegex = /^[\p{L}\p{M}\p{N}\s\p{P}\p{Sc}+=<>~^|`]*$/u;

  if (!allowedRegex.test(inputValue)) {
    return SPECIAL_CHARS_MSG;
  }

  if (inputValue.length > maxLength) {
    return `Maximum ${maxLength} characters allowed`;
  }

  return null;
}

// Email characters, format and length (an address can be up to 254 characters)
export function validateEmailField(inputValue, maxLength = 254) {
  if (inputValue === undefined || inputValue == null || inputValue === "") {
    return null;
  }
  if (inputValue.length > maxLength) {
    return `Maximum ${maxLength} characters allowed`;
  }
  // Regex: only allow specified characters
  const allowedRegex = /^[A-Za-z0-9._%+'\-@]+$/;
  if (!allowedRegex.test(inputValue)) {
    return "Invalid email characters detected";
  }

  const parts = inputValue.split('@');
  if (parts.length !== 2) return "Invalid email format.";
  const [local, domain] = parts;

  // local part: 1-64 chars, no leading/trailing/double dots
  if (!local || local.length > 64 || local.startsWith('.') || local.endsWith('.') || local.includes('..')) {
    return "Invalid email format.";
  }
  // domain: labels of letters/digits/hyphens that do not start or end with a hyphen, TLD of 2+ letters
  const domainRegex = /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
  if (!domainRegex.test(domain)) {
    return "Invalid email format.";
  }

  return null; // valid
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const isValidEmail = (value) =>
  typeof value === 'string' && EMAIL_REGEX.test(value.trim());

// Required + format, in one call
export const requiredName = (value, label, maxLength = 25) =>
  requiredError(value, label) ||
  nameOnChangeValidator(value, maxLength) ||
  (HAS_LETTER.test(value) ? null : `${label} must include at least one letter.`);

// Company name / job title: required, allowed characters, at least one letter
export const requiredCompany = (value, label, maxLength = 50) =>
  requiredError(value, label) ||
  companyOnChangeValidator(value, maxLength) ||
  (HAS_LETTER.test(value) ? null : `${label} must include at least one letter.`);

// Optional company name: only checked when something is typed
export const optionalCompany = (value, label = 'Company', maxLength = 50) =>
  hasText(value)
    ? companyOnChangeValidator(value, maxLength) ||
      (HAS_LETTER.test(value) ? null : `${label} must include at least one letter.`)
    : null;

export const requiredSubject = (value, label = 'Subject', maxLength = 100) =>
  requiredError(value, label) || subjectOnChangeValidator(value, maxLength);

export const requiredText = (value, label, maxLength = 2500) =>
  requiredError(value, label) || validateDescriptionField(value, maxLength);

// Email: required + characters/format/length (validateEmailField).
// With workOnly, personal addresses (gmail, yahoo...) are rejected too,
// using the PERSONAL_DOMAINS set from data/mockData.
// Regional variants (yahoo.com.au, hotmail.fr, outlook.com.au...) are caught by the pattern below.
const PERSONAL_DOMAIN_PATTERN =
  /^(?:gmail|googlemail|yahoo|ymail|rocketmail|hotmail|outlook|live|msn|aol|icloud|protonmail|gmx|yandex)\.(?:com|net|org|[a-z]{2}|(?:co|com|net|org)\.[a-z]{2})$/;

export const emailError = (value, { workOnly = false, personalDomains } = {}) => {
  if (!hasText(value)) return 'Email is required.';
  const email = value.trim();
  const formatError = validateEmailField(email);
  if (formatError) return formatError;
  if (workOnly) {
    const domain = email.split('@')[1].toLowerCase();
    if ((personalDomains && personalDomains.has(domain)) || PERSONAL_DOMAIN_PATTERN.test(domain)) {
      return 'Please enter your work email to continue';
    }
  }
  return null;
};

// Optional phone number for the enquiry forms: digits with optional + ( ) - . and spaces, 7-15 digits
export const optionalPhoneError = (value) => {
  if (!hasText(value)) return null;
  const v = value.trim();
  if (!/^\+?[\d\s().-]+$/.test(v)) return 'Phone number can only contain digits, spaces and + ( ) - .';
  const digits = v.replace(/\D/g, '');
  if (digits.length < 7) return 'Phone number is too short.';
  if (digits.length > 15) return 'Phone number is too long.';
  return null;
};

// Optional website: must be a full http(s) address
export const optionalWebsiteError = (value, maxLength = 200) => {
  if (!hasText(value)) return null;
  const v = value.trim();
  if (v.length > maxLength) return `Maximum ${maxLength} characters allowed`;
  if (/\s/.test(v)) return 'Website cannot contain spaces.';
  let url;
  try {
    url = new URL(v);
  } catch (e) {
    return 'Enter a full website address, e.g. https://yourcompany.com';
  }
  if (!['http:', 'https:'].includes(url.protocol)) return 'Website must start with http:// or https://';
  if (!/^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i.test(url.hostname)) {
    return 'Enter a valid website address, e.g. https://yourcompany.com';
  }
  return null;
};

// Red border classes, added to a field when it has an error
export const errorClass = (message) =>
  message
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500 focus-visible:border-red-500 focus-visible:ring-red-500'
    : '';

// Accessibility attributes that link a field to its error message (spread onto the input)
export const fieldA11y = (id, message) => ({
  'aria-invalid': message ? true : undefined,
  'aria-describedby': message ? `${id}-error` : undefined,
});

// Escapes every string value of the form data (for use inside the HTML email template)
export const escapeFormData = (data) =>
  Object.fromEntries(Object.entries(data).map(([key, value]) => [key, escapeHtml(value)]));
